import { AlertMessage, FullPageCard, NavigationPage } from '@/app/components'
// Removed ref tracking for initial animation suppression; relying on motion settings
import useViewModel from './useInactiveTimesViewModel'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import { Plus } from 'lucide-react'
import Button from '@/components/Button/Button'
import InactiveTimeRow from './InactiveTimeRow'
import { motion, AnimatePresence } from 'motion/react'
import Modal from '@/components/Modal'
import { useHistory } from 'react-router-dom'

export default function ThermostatCostCategoriesInactiveTimes() {
  const viewModel = useViewModel()
  const history = useHistory()

  return (
    <FullPageCard>
      <NavigationPage
        id="cost-categories-inactive-times"
        title="Set Inactive Times"
        subtitle="Set time(s) when you want Save Money to be inactive."
        className="p-0"
        navigationOptions={['back']}
        backOverride={() =>
          viewModel.onNavigationAttempt(() => history.goBack())
        }
      >
        <LoadingContainer
          isLoading={viewModel.isLoading}
          loadedContent={<InactiveTimesContent {...viewModel} />}
        />
      </NavigationPage>

      {/* Navigation Confirmation Modal */}
      <Modal
        id="confirm-navigation"
        open={viewModel.showConfirmationModal}
        onClose={viewModel.onCancelNavigation}
        title="Do you want to save your edits?"
        maxWidth="440px"
      >
        <AlertMessage variant="info">
          {viewModel.scheduleSummary.length > 0 ? (
            <div>
              <div className="text-sm font-medium text-base-grey-700">
                Save Money will be inactive:
              </div>
              <div className="">
                {viewModel.scheduleSummary.map((scheduleText, index) => (
                  <div
                    key={index}
                    className="text-sm font-bold text-base-grey-600"
                  >
                    {scheduleText}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>Save Money is currently running at all times</div>
          )}
        </AlertMessage>

        <Modal.Actions>
          <Button
            id="discard-navigation"
            variant="secondary"
            onClick={viewModel.onDiscardAndNavigate}
          >
            Discard
          </Button>
          <Button
            id="confirm-navigation"
            variant="primary"
            onClick={viewModel.onSaveAndNavigate}
          >
            Confirm
          </Button>
        </Modal.Actions>
      </Modal>
    </FullPageCard>
  )
}

function InactiveTimesContent({
  inactiveTimes,
  onAddInactiveTime,
  onRemoveInactiveTime,
  onUpdateInactiveTime,
  onToggleDay,
  onSave,
  isLoading,
  isSaveDisabled,
  isAddDisabled,
  scheduleSummary,
}: ReturnType<typeof useViewModel>) {
  return (
    <div className="flex flex-col h-full gap-4">
      <AlertMessage variant="info">
        {scheduleSummary.length > 0 ? (
          <div>
            <div className="text-sm font-medium text-base-grey-700">
              Save Money will be inactive:
            </div>
            <div className="">
              {scheduleSummary.map((scheduleText, index) => (
                <div
                  key={index}
                  className="text-sm font-bold text-base-grey-600"
                >
                  {scheduleText}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>Save Money is currently running at all times</div>
        )}
      </AlertMessage>

      <motion.div
        className="flex flex-col gap-3"
        layout
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <AnimatePresence initial={false}>
          {inactiveTimes.map((row) => (
            <motion.div
              key={row.id}
              layout
              initial={{ opacity: 0, y: 4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{
                opacity: 0,
                y: 4,
                scale: 0.98,
              }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <InactiveTimeRow
                value={row}
                onRemove={onRemoveInactiveTime}
                onChange={onUpdateInactiveTime}
                onToggleDay={onToggleDay}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <motion.div
        layout
        transition={{ duration: 0.15 }}
        id="add-inactive-time"
        onClick={isAddDisabled ? undefined : onAddInactiveTime}
        className={`flex items-center justify-center w-full ${
          isAddDisabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:cursor-pointer'
        }`}
      >
        <div className="flex items-center justify-center w-8 h-8 mr-3 border-none rounded-lg bg-themed-blue-100">
          <Plus size={16} className="text-themed-blue-500" />
        </div>
        <span className="text-sm text-base-grey-500">Add inactive time</span>
      </motion.div>

      {/* Spacer to push save button to bottom */}
      <div className="flex-1" />

      {/* Save button */}
      <Button
        id="save-inactive-times"
        variant="primary"
        className="mt-6"
        disabled={isSaveDisabled || isLoading}
        onClick={onSave}
      >
        Save
      </Button>
    </div>
  )
}
